import React, { useState } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import css from './CategoryContentSection.module.css';

// How long a description may be before it folds behind "Read more", by screen width. The
// whole text is always in the page (search engines read it); CSS folds it, so the server's
// page and the browser's agree.
const FOLD_AFTER_PHONE = 150;
const FOLD_AFTER_TABLET = 250;
const FOLD_AFTER_DESKTOP = 400;

const PlayIcon = () => (
  <div className={css.playButton}>
    <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
      <path d="M8 5v14l11-7z" />
    </svg>
  </div>
);

const YouTubeFacade = ({ youtubeId, customThumbnail }) => {
  const [playing, setPlaying] = useState(false);
  const intl = useIntl();

  if (playing) {
    return (
      <div className={css.videoContainer}>
        <iframe
          className={css.video}
          src={`https://www.youtube.com/embed/${youtubeId}?rel=0&autoplay=1`}
          title={intl.formatMessage({ id: 'CategoryContentSection.videoTitle' })}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        ></iframe>
      </div>
    );
  }

  const thumbnailSrc = customThumbnail || `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;

  return (
    <button className={css.videoFacade} onClick={() => setPlaying(true)} type="button">
      <img
        className={css.videoThumbnail}
        src={thumbnailSrc}
        alt={intl.formatMessage({ id: 'CategoryContentSection.playVideo' })}
        loading="lazy"
      />
      <PlayIcon />
    </button>
  );
};

const VimeoFacade = ({ vimeoId }) => {
  const [playing, setPlaying] = useState(false);
  const intl = useIntl();

  if (playing) {
    return (
      <div className={css.videoContainer}>
        <iframe
          className={css.video}
          src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1`}
          title={intl.formatMessage({ id: 'CategoryContentSection.videoTitle' })}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        ></iframe>
      </div>
    );
  }

  return (
    <button className={css.videoFacade} onClick={() => setPlaying(true)} type="button">
      <img
        className={css.videoThumbnail}
        src={`https://vumbnail.com/${vimeoId}.jpg`}
        alt={intl.formatMessage({ id: 'CategoryContentSection.playVideo' })}
        loading="lazy"
      />
      <PlayIcon />
    </button>
  );
};

/**
 * CategoryContentSection component
 * Renders optional content section with description and media (image or video)
 *
 * Layout:
 * - Desktop: Two-column (description left, media right)
 * - Mobile: Stacked (media on top, description below)
 * - If no media: Full-width description
 */
const CategoryContentSection = props => {
  const { heading, description, heroImage, videoUrl, mediaAttribution, mediaAttributionName, mediaAttributionUrl, className } = props;

  const [showFullDescription, setShowFullDescription] = useState(false);

  const hasMedia = heroImage || videoUrl;
  const hasDescription = description && description.trim().length > 0;

  if (!hasMedia && !hasDescription) {
    return null;
  }

  const rootClassName = classNames(css.root, className, {
    [css.withMedia]: hasMedia,
    [css.descriptionOnly]: !hasMedia && hasDescription,
  });

  // Extract YouTube video ID from URL
  const getYouTubeId = url => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Extract Vimeo video ID from URL
  const getVimeoId = url => {
    if (!url) return null;
    const regExp = /(?:vimeo\.com\/)(?:video\/)?(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  const youtubeId = getYouTubeId(videoUrl);
  const vimeoId = !youtubeId ? getVimeoId(videoUrl) : null;

  const length = hasDescription ? description.length : 0;
  const canFold = length > FOLD_AFTER_PHONE;
  const foldClasses = classNames(css.descriptionWrapper, {
    [css.foldPhone]: length > FOLD_AFTER_PHONE,
    [css.foldTablet]: length > FOLD_AFTER_TABLET,
    [css.foldDesktop]: length > FOLD_AFTER_DESKTOP,
    [css.unfolded]: showFullDescription,
  });

  return (
    <section className={rootClassName}>
      {/* Text content: heading + description */}
      <div className={foldClasses}>
        {heading && (
          <>
            <span className={css.eyebrow}>
              <FormattedMessage id="CategoryContentSection.eyebrow" />
            </span>
            <h1 className={css.heading}>{heading}</h1>
          </>
        )}
        {hasDescription && <p className={css.description}>{description}</p>}
        {/* Shown by CSS only on the widths where this description folds */}
        {canFold && (
          <button
            className={css.readMoreButton}
            onClick={() => setShowFullDescription(!showFullDescription)}
            type="button"
            aria-expanded={showFullDescription}
          >
            <FormattedMessage
              id={
                showFullDescription
                  ? 'CategoryContentSection.readLess'
                  : 'CategoryContentSection.readMore'
              }
            />
          </button>
        )}
      </div>

      {/* Media: Image or Video */}
      {hasMedia && (
        <div className={css.mediaWrapper}>
          {videoUrl && youtubeId ? (
            <YouTubeFacade youtubeId={youtubeId} customThumbnail={heroImage} />
          ) : videoUrl && vimeoId ? (
            <VimeoFacade vimeoId={vimeoId} />
          ) : heroImage ? (
            <div className={css.imageContainer}>
              <img
                className={css.image}
                src={heroImage}
                alt=""
                loading="lazy"
              />
            </div>
          ) : null}
          {mediaAttribution && mediaAttributionName && (
            <p className={css.mediaAttribution}>
              {mediaAttribution}:{' '}
              {mediaAttributionUrl ? (
                <a
                  href={mediaAttributionUrl}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className={css.mediaAttributionLink}
                >
                  {mediaAttributionName}
                  <img src="/static/icons/building-store.svg" alt="" className={css.mediaAttributionIcon} />
                </a>
              ) : (
                <span>{mediaAttributionName}</span>
              )}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

CategoryContentSection.defaultProps = {
  heading: null,
  description: null,
  heroImage: null,
  videoUrl: null,
  mediaAttribution: null,
  mediaAttributionName: null,
  mediaAttributionUrl: null,
  className: null,
};

CategoryContentSection.propTypes = {
  heading: string,
  description: string,
  heroImage: string,
  videoUrl: string,
  mediaAttribution: string,
  mediaAttributionName: string,
  mediaAttributionUrl: string,
  className: string,
};

export default CategoryContentSection;
